import { Action } from 'redux-actions'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { t } from 'ttag'
import api from '../../../services/api.services'
import {
  GetUserActivities$Request,
  GetActivityRoute$Request,
  DeleteActivity$Request,
  GetActivityDuplicates$Request,
  SetMainActivity$Request,
} from '../../../services/api.types'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import {
  getUserActivities,
  getUserActivitiesFail,
  getUserActivitiesSuccess,
  loadMoreActivities,
  loadMoreActivitiesSuccess,
  loadMoreActivitiesFail,
  loadActivityRoute,
  loadActivityRouteSuccess,
  loadActivityRouteFail,
  deleteActivity,
  deleteActivitySuccess,
  deleteActivityFail,
  openReviewDuplicates,
  loadDuplicatesSuccess,
  loadDuplicatesFail,
  setMainActivity,
  setMainActivitySuccess,
  setMainActivityFail,
} from './ActivitiesActions'
import { getDuplicatesMainId, getRoutesById } from './ActivitiesSelectors'

function* sagaGetUserActivities(action: Action<GetUserActivities$Request>) {
  try {
    const { userActivities } = yield call(api.users.getUserActivities, action.payload)
    yield put({
      type: String(getUserActivitiesSuccess),
      payload: userActivities,
    })
  } catch (e: any) {
    console.log('GetUserActivitiesFail: ', e)
    yield put({ type: String(getUserActivitiesFail) })
  }
}

// New saga for loading more activities
function* sagaLoadMoreActivities(action: Action<GetUserActivities$Request>) {
  try {
    const { userActivities } = yield call(api.users.getUserActivities, action.payload)
    yield put({
      type: String(loadMoreActivitiesSuccess),
      payload: userActivities,
    })
  } catch (e: any) {
    console.log('LoadMoreActivitiesFail: ', e)
    yield put({ type: String(loadMoreActivitiesFail) })
  }
}

// Saga for lazily fetching one activity's GPS route on first expand.
function* sagaLoadActivityRoute(action: Action<GetActivityRoute$Request>) {
  const { activityId } = action.payload
  try {
    // Already cached (e.g. re-expand) — nothing to do.
    const routesById: { [id: string]: unknown } = yield select(getRoutesById)
    if (routesById[activityId]) return

    const { routes } = yield call(api.activity.getActivityRoute, { activityId })
    yield put({ type: String(loadActivityRouteSuccess), payload: { activityId, routes } })
  } catch (e: any) {
    console.log('LoadActivityRouteFail: ', e)
    yield put({ type: String(loadActivityRouteFail), payload: { activityId } })
    const errorNotification: INotification = {
      type: 'warning',
      content: t`Failed to load the route. Please try again.`,
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

// Saga for deleting an activity
function* sagaDeleteActivity(action: Action<DeleteActivity$Request>) {
  try {
    const { success } = yield call(api.activity.deleteActivity, action.payload)
    if (success) {
      yield put({ type: String(deleteActivitySuccess), payload: action.payload.activityId })

      // Show success notification
      const successNotification: INotification = {
        type: 'success',
        content: t`Activity deleted successfully!`,
      }
      yield put({ type: String(addNotification), payload: successNotification })
    } else {
      throw new Error('Delete activity failed')
    }
  } catch (e: any) {
    console.log('DeleteActivityFail: ', e)
    yield put({ type: String(deleteActivityFail), payload: e.message })

    // Show error notification
    const errorNotification: INotification = {
      type: 'warning',
      content: t`Failed to delete activity. Please try again.`,
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

// Saga for opening the review-duplicates modal: fetch the whole group for the clicked activity.
function* sagaOpenReviewDuplicates(action: Action<GetActivityDuplicates$Request>) {
  try {
    const { activities, mainActivityId } = yield call(api.activity.getActivityDuplicates, action.payload)
    yield put({ type: String(loadDuplicatesSuccess), payload: { activities, mainActivityId } })
  } catch (e: any) {
    console.log('LoadDuplicatesFail: ', e)
    yield put({ type: String(loadDuplicatesFail) })
    const errorNotification: INotification = {
      type: 'warning',
      content: t`Failed to load duplicates. Please try again.`,
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

// Saga for promoting an activity to the main of its duplicate group.
function* sagaSetMainActivity(action: Action<SetMainActivity$Request>) {
  try {
    const previousMainId: string | null = yield select(getDuplicatesMainId)
    const { activities, mainActivityId } = yield call(api.activity.setMainActivity, action.payload)
    yield put({
      type: String(setMainActivitySuccess),
      payload: { activities, mainActivityId, previousMainId },
    })
    const successNotification: INotification = {
      type: 'success',
      content: t`Main activity updated!`,
    }
    yield put({ type: String(addNotification), payload: successNotification })
  } catch (e: any) {
    console.log('SetMainActivityFail: ', e)
    yield put({ type: String(setMainActivityFail) })
    const errorNotification: INotification = {
      type: 'warning',
      content: t`Failed to update main activity. Please try again.`,
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

export function* activitiesSagas() {
  yield all([
    // takeLatest: a filter/search change supersedes any in-flight fetch, so a slower earlier
    // response can't clobber the list with stale results (the filter chips getting "stuck").
    takeLatest(String(getUserActivities), sagaGetUserActivities),
    takeEvery(String(loadMoreActivities), sagaLoadMoreActivities),
    takeEvery(String(loadActivityRoute), sagaLoadActivityRoute),
    takeEvery(String(deleteActivity), sagaDeleteActivity),
    takeEvery(String(openReviewDuplicates), sagaOpenReviewDuplicates),
    takeEvery(String(setMainActivity), sagaSetMainActivity),
  ])
}
